import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { StorageService } from '../../../core/services/storage/storage.service';
import { ICart, ICartLine, IProduct } from '../../interfaces/product.interface';
import { productMainImage, sellerKey } from '../../utils/product.utils';

const CART_STORAGE_KEY = 'SHOP_CART';

export type AddToCartResult = 'added' | 'seller-mismatch' | 'out-of-stock';

/**
 * Panier de la boutique, conservé dans le navigateur (localStorage via
 * StorageService, qui ne fait rien côté serveur). Il n'existe pas de panier
 * côté API : le contenu n'est envoyé qu'au moment de commander.
 *
 * Une commande ne peut contenir que les produits d'un seul vendeur (règle du
 * backend) : ajouter un produit d'un autre vendeur renvoie 'seller-mismatch',
 * à l'appelant de proposer de vider le panier.
 */
@Injectable({
  providedIn: 'root'
})
export class CartService {
  readonly #storage: StorageService = inject(StorageService);

  #cart: WritableSignal<ICart | null> = signal<ICart | null>(this.#storage.getObject<ICart>(CART_STORAGE_KEY));
  #isOpen: WritableSignal<boolean> = signal(false);

  cart: Signal<ICart | null> = this.#cart.asReadonly();
  isOpen: Signal<boolean> = this.#isOpen.asReadonly();
  itemCount: Signal<number> = computed(() => (this.#cart()?.lines ?? []).reduce((sum, l) => sum + l.quantity, 0));
  subtotal: Signal<number> = computed(() => (this.#cart()?.lines ?? []).reduce((sum, l) => sum + l.price * l.quantity, 0));
  currency: Signal<string> = computed(() => this.#cart()?.lines[0]?.currency ?? 'CAD');

  open(): void {
    this.#isOpen.set(true);
  }

  close(): void {
    this.#isOpen.set(false);
  }

  quantityOf(productId: number): number {
    return this.#cart()?.lines.find(l => l.productId === productId)?.quantity ?? 0;
  }

  add(product: IProduct, quantity: number = 1, replaceCart: boolean = false): AddToCartResult {
    const stock = product.stock_quantity;
    if (stock !== null && stock !== undefined && stock <= 0) return 'out-of-stock';

    const key = sellerKey(product);
    let cart = this.#cart();
    if (cart && cart.sellerKey !== key) {
      if (!replaceCart) return 'seller-mismatch';
      cart = null;
    }

    const lines: ICartLine[] = cart ? [...cart.lines] : [];
    const index = lines.findIndex(l => l.productId === product.id);
    const current = index >= 0 ? lines[index].quantity : 0;
    const next = this.#clamp(current + quantity, stock);
    const line: ICartLine = {
      productId: product.id,
      name: product.name,
      price: Number(product.price),
      currency: product.currency || 'CAD',
      imageUrl: productMainImage(product),
      stock: stock ?? null,
      quantity: next
    };
    if (index >= 0) lines[index] = line; else lines.push(line);

    this.#persist({ sellerKey: key, sellerName: product.seller_name ?? '', lines });
    return 'added';
  }

  setQuantity(productId: number, quantity: number): void {
    const cart = this.#cart();
    if (!cart) return;
    if (quantity <= 0) {
      this.remove(productId);
      return;
    }
    const lines = cart.lines.map(l => l.productId === productId ? { ...l, quantity: this.#clamp(quantity, l.stock) } : l);
    this.#persist({ ...cart, lines });
  }

  remove(productId: number): void {
    const cart = this.#cart();
    if (!cart) return;
    const lines = cart.lines.filter(l => l.productId !== productId);
    this.#persist(lines.length ? { ...cart, lines } : null);
  }

  clear(): void {
    this.#persist(null);
  }

  #clamp(quantity: number, stock: number | null | undefined): number {
    const max = stock === null || stock === undefined ? 99 : stock;
    return Math.max(1, Math.min(quantity, max));
  }

  #persist(cart: ICart | null): void {
    this.#cart.set(cart);
    if (cart) this.#storage.setObject(CART_STORAGE_KEY, cart);
    else this.#storage.removeItem(CART_STORAGE_KEY);
  }
}
